import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CustomizeOfficeDialog } from "./CustomizeOfficeDialog";
import { LaptopScreen } from "./LaptopScreen";
import { MeetingModeOverlay } from "./MeetingModeOverlay";
import { OnboardingModal } from "./OnboardingModal";
import { ActionDock } from "./scene/ActionDock";
import { SceneAppBar } from "./scene/SceneAppBar";
import { SceneControls } from "./scene/SceneControls";
import { SceneDock } from "./scene/SceneDock";
import { LaptopIcon } from "./scene/scene-icons";
import { useVyllageState } from "./use-vyllage-state";
import { VyllageWorldHost } from "./vyllage-elements";
import "./scene.css";

export function VyllageOffice() {
  const s = useVyllageState();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { ready?: "1" };
  const autoEntering = search.ready === "1";

  const leaveHq = () => {
    // Unmount tears down multiplayer (use-office-multiplayer cleanup).
    void navigate({ to: "/dashboard/avatar" });
  };

  const nextUnlockLabel = s.nextUnlock
    ? `${s.nextUnlock.name} · ${s.nextUnlock.pts} pts`
    : "All decor unlocked";

  return (
    <div
      className="vy-scene flex h-dvh w-full flex-col overflow-hidden bg-[var(--bg-3)] text-[var(--fg-1)]"
      style={{ fontFamily: "var(--font-body)" }}
    >
      <SceneAppBar s={s} onLeave={leaveHq} />

      {/* Body */}
      <div className="relative flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <VyllageWorldHost onReady={s.onWorldReady} />

          <div className="pointer-events-none absolute bottom-3.5 left-3.5 max-w-[min(440px,70vw)] rounded-full bg-[var(--vy-hint)] px-3.5 py-1.5 text-[11px] font-medium tracking-wide text-white/85">
            {s.inOwnOffice
              ? "Your desk · open laptop for apps · sit for meetings"
              : "Click a chair to sit · meeting view · scroll zoom · E wave"}
          </div>

          <ActionDock s={s} />
          <SceneControls worldRef={s.worldRef} />

          {/* Floating hint when you enter your own pod */}
          {s.inOwnOffice && !s.laptopOpen && !s.meetingViewOpen ? (
            <button
              type="button"
              onClick={() => {
                s.setMeetingViewOpen(false);
                s.setLaptopOpen(true);
              }}
              className="absolute top-3.5 left-3.5 z-30 flex cursor-pointer items-center gap-2 rounded-xl border border-white/15 bg-[var(--vy-dock)] px-3 py-2 text-left shadow-lg backdrop-blur-sm hover:border-[var(--vy-accent)]/50 hover:bg-[var(--vy-bar)]"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--vy-accent)]/25 text-[var(--vy-accent-fg)]">
                <LaptopIcon />
              </span>
              <span>
                <span className="block text-xs font-bold text-white">Open laptop</span>
                <span className="block text-[10px] text-white/55">Slack, email, tools…</span>
              </span>
            </button>
          ) : null}

          {/* Toasts — above meeting overlay (z-55) so share/media errors are visible while seated */}
          <div className="pointer-events-none absolute top-3.5 left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2">
            {s.toasts.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-2 rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-[18px] py-2 text-[12.5px] font-semibold text-[var(--vy-surface-fg)] shadow-[var(--shadow-md)]"
                style={{ animation: "vyToast 0.25s var(--ease-out)" }}
              >
                <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--vy-accent)]" />
                <span>{t.text}</span>
              </div>
            ))}
          </div>

          {/* Seated but minimized — re-open Zoom grid */}
          {s.meeting.sitting && s.meeting.inside && !s.meetingViewOpen ? (
            <button
              type="button"
              onClick={s.openMeetingView}
              className="absolute top-3.5 right-3.5 z-30 flex w-[248px] cursor-pointer flex-col overflow-hidden rounded-xl border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] text-left shadow-[var(--shadow-md)] hover:ring-2 hover:ring-[var(--vy-accent)]/40"
            >
              <div className="flex items-center gap-1.5 px-3 py-2">
                <span
                  className={`h-[7px] w-[7px] rounded-full ${s.meeting.live ? "animate-pulse bg-[var(--vy-bad)]" : "bg-[var(--vy-accent)]"}`}
                />
                <span className="text-[11.5px] font-bold text-[var(--vy-surface-fg)]">
                  {s.meeting.live
                    ? s.meeting.presenter
                      ? `${s.meeting.presenter.split(" ")[0]} in room`
                      : "Room live"
                    : "Seated"}
                </span>
                <span className="ml-auto max-w-[90px] truncate text-[10px] tracking-wide text-[var(--vy-surface-fg-3)] uppercase">
                  {s.meeting.topic || "Room"}
                </span>
              </div>
              <div className="bg-[var(--vy-meeting-strip)] px-3 py-3">
                <div
                  className="text-xs font-bold text-white"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Open meeting view
                </div>
                <p className="mt-0.5 text-[11px] text-white/60">Video grid · private room audio</p>
              </div>
            </button>
          ) : null}

          <MeetingModeOverlay
            open={s.meetingViewOpen && s.meeting.sitting && s.meeting.inside}
            roomName={s.meeting.topic || s.zone}
            personal={s.meeting.personal}
            live={s.meeting.live}
            selfName={s.name.trim() || "You"}
            selfColor={s.colors.shirt}
            selfStream={s.localStream}
            selfCamOn={s.cam}
            selfMicOn={s.mic}
            selfSharing={s.sharing}
            participants={s.meeting.participants.map((p) => ({
              ...p,
              stream: s.remoteStreams[p.id] ?? null,
            }))}
            mic={s.mic}
            cam={s.cam}
            onToggleMic={s.toggleMic}
            onToggleCam={s.toggleCam}
            onToggleShare={() => {
              void s.toggleScreenShare();
            }}
            onStandUp={s.standUp}
            onMinimize={s.minimizeMeetingView}
          />

          <LaptopScreen
            open={s.laptopOpen && s.inOwnOffice}
            onClose={() => s.setLaptopOpen(false)}
            deskName={s.myOffice?.displayName ?? "My desk"}
          />

          {s.showOnboarding ? (
            <OnboardingModal
              name={s.name}
              onNameChange={s.setName}
              colors={s.colors}
              onColorChange={s.updateColors}
              onColorsReplace={s.replaceColors}
              entered={s.entered}
              onEnter={() => {
                if (s.entered) {
                  s.setShowOnboarding(false);
                  return;
                }
                s.enterOffice();
              }}
              onClose={() => s.setShowOnboarding(false)}
            />
          ) : null}

          {/* Look already saved — light enter gate when not auto-entering via ?ready=1. */}
          {!s.showOnboarding && !s.entered && !autoEntering ? (
            <div className="absolute inset-0 z-[70] flex items-center justify-center bg-[var(--vy-scrim)] backdrop-blur-[4px]">
              <div className="flex w-[min(360px,92vw)] flex-col items-center gap-3 rounded-2xl bg-[var(--vy-surface)] px-7 py-6 text-center shadow-[var(--shadow-lg)]">
                <img src="/assets/nymbl-fox-blue.png" alt="" className="h-10" />
                <div
                  className="text-lg font-bold text-[var(--vy-surface-fg)]"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Ready for HQ?
                </div>
                <p className="text-[12.5px] leading-relaxed text-[var(--vy-surface-fg-2)]">
                  Your look is set. Click to join multiplayer and enable camera & mic.
                </p>
                <button
                  type="button"
                  onClick={s.enterOffice}
                  className="w-full cursor-pointer rounded-full bg-[var(--vy-bar)] py-3 text-sm font-bold tracking-wide text-white hover:bg-[var(--vy-bar-deep)]"
                >
                  Enter The Office
                </button>
              </div>
            </div>
          ) : null}

          {/* Seamless join from Ready room while profile/world hydrates */}
          {!s.entered && autoEntering ? <AutoEnterOverlay onEnter={s.enterOffice} /> : null}

          {s.myOffice ? (
            <CustomizeOfficeDialog
              open={s.showCustomizeOffice}
              onOpenChange={s.setShowCustomizeOffice}
              displayName={s.myOffice.displayName}
              doorStatus={s.myOffice.doorStatus}
              decorIds={s.myOffice.decorIds}
              wallArtFileId={s.myOffice.wallArtFileId}
              wallArtPlaceholder={s.myOffice.wallArtPlaceholder}
              theme={s.myOffice.theme}
              unlockedIds={s.unlockedIds}
              points={s.points}
              isSaving={s.officeDoorPending}
              onSave={s.saveOfficeCustomization}
            />
          ) : null}
        </div>

        <SceneDock
          tab={s.tab}
          onTabChange={s.setTab}
          feed={s.feed}
          feedElRef={s.feedElRef}
          chatInputRef={s.chatInputRef}
          onSendChat={s.sendChat}
          people={s.people}
          onWalkTo={s.walkTo}
          onWalkToOffice={s.walkToOffice}
          points={s.points}
          streak={s.streak}
          unlockedIds={s.unlockedIds}
          nextUnlockLabel={nextUnlockLabel}
          progressPct={s.progressPct}
          officeByOwnerId={s.officeByOwnerId}
          offices={s.officeSnapshots}
          myOffice={s.myOffice}
        />
      </div>
    </div>
  );
}

/** Overlay while ?ready=1 auto-join runs; offers a manual escape if it stalls. */
function AutoEnterOverlay({ onEnter }: { onEnter: () => void }) {
  const [showFallback, setShowFallback] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShowFallback(true), 3000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="absolute inset-0 z-[65] flex items-center justify-center bg-[var(--vy-scrim)]">
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-[var(--vy-surface)] px-6 py-5 shadow-[var(--shadow-md)]">
        <p className="text-sm font-semibold text-[var(--vy-surface-fg)]">Stepping into HQ…</p>
        {showFallback ? (
          <>
            <p className="max-w-[260px] text-center text-[12px] text-[var(--vy-surface-fg-2)]">
              Taking longer than usual — you can join manually.
            </p>
            <button
              type="button"
              onClick={onEnter}
              className="cursor-pointer rounded-full bg-[var(--vy-bar)] px-5 py-2.5 text-sm font-bold text-white hover:bg-[var(--vy-bar-deep)]"
            >
              Enter The Office
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
